import React, { useRef, useState } from "react";

import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import CalendarEventBlock, { BlockGestureMode } from "./CalendarEventBlock";
import { IntervalEdit } from "./IntervalPopover";
import { useSchedule } from "../SchedulerProvider";
import Interval from "../store/Interval";
import Rule from "../store/Rule";
import {
  DAY_MINUTES,
  HOUR_HEIGHT,
  MIN_DURATION_MINUTES,
  intervalMinutes,
  minutesToOffset,
  offsetToMinutes,
  snapMinutes,
  timeOfDayFromMinutes,
} from "../utils/calendar";

type Props = {
  date: Date;
  rule: Rule;
  isOverride: boolean;
  isToday: boolean;
  onEditInterval: (edit: IntervalEdit) => void;
  // Snapshot the schedule for undo just before a gesture actually mutates it.
  onBeforeMutate: () => void;
  // Highlighted because a block is being dragged over this column.
  isDropTarget?: boolean;
  // Report which day (ISO) the pointer is over during a move, so the grid can
  // highlight the drop target; null when back over the source day.
  onHoverDate?: (iso: string | null) => void;
};

type Gesture =
  | { kind: "create"; anchorMin: number }
  | {
      kind: "move";
      interval: Interval;
      anchorMin: number;
      origStart: number;
      origEnd: number;
      element: HTMLElement;
      // ISO of the day column the pointer is currently over (for cross-day move).
      targetDate?: string;
    }
  | { kind: "resize-start"; interval: Interval; origEnd: number }
  | { kind: "resize-end"; interval: Interval; origStart: number };

const COLUMN_HEIGHT = (DAY_MINUTES / 60) * HOUR_HEIGHT;

const CalendarDayColumn: React.FC<Props> = observer(
  ({
    date,
    rule,
    isOverride,
    isToday,
    onEditInterval,
    onBeforeMutate,
    isDropTarget,
    onHoverDate,
  }) => {
    const { classes, cx } = useStyles();
    const schedule = useSchedule();
    const containerRef = useRef<HTMLDivElement>(null);
    const gestureRef = useRef<Gesture | null>(null);
    // Whether this gesture has already snapshotted for undo (lazily, on the
    // first real change) so a click that opens the popover adds no undo step.
    const pushedRef = useRef(false);
    const [draft, setDraft] = useState<{ start: number; end: number } | null>(
      null
    );

    const snapshotOnce = () => {
      if (!pushedRef.current) {
        onBeforeMutate();
        pushedRef.current = true;
      }
    };

    const pointerMinutes = (clientY: number): number => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return 0;
      return snapMinutes(offsetToMinutes(clientY - rect.top));
    };

    // Which day column is under the pointer. The source column holds the
    // pointer capture, so the event target is always this column — hit-test the
    // document instead to find the day being hovered for a cross-day move.
    const dateAtPoint = (clientX: number, clientY: number): string | null => {
      const el = document
        .elementFromPoint(clientX, clientY)
        ?.closest("[data-date]");
      return el?.getAttribute("data-date") ?? null;
    };

    const capture = (event: React.PointerEvent) => {
      try {
        containerRef.current?.setPointerCapture(event.pointerId);
      } catch {
        // setPointerCapture can throw if the pointer is no longer active;
        // the gesture still works via the container's pointer handlers.
      }
    };

    const handleBackgroundPointerDown = (event: React.PointerEvent) => {
      if (event.button !== 0) return;
      pushedRef.current = false;
      const anchorMin = pointerMinutes(event.clientY);
      gestureRef.current = { kind: "create", anchorMin };
      setDraft({ start: anchorMin, end: anchorMin });
      capture(event);
    };

    const handleGestureStart = (
      event: React.PointerEvent,
      mode: BlockGestureMode,
      interval: Interval
    ) => {
      if (event.button !== 0) return;
      event.stopPropagation();
      pushedRef.current = false;
      const minutes = intervalMinutes(interval);
      if (!minutes) return;

      if (mode === "move") {
        gestureRef.current = {
          kind: "move",
          interval,
          anchorMin: pointerMinutes(event.clientY),
          origStart: minutes.start,
          origEnd: minutes.end,
          element: event.currentTarget as HTMLElement,
        };
      } else if (mode === "resize-start") {
        gestureRef.current = {
          kind: "resize-start",
          interval,
          origEnd: minutes.end,
        };
      } else {
        gestureRef.current = {
          kind: "resize-end",
          interval,
          origStart: minutes.start,
        };
      }
      capture(event);
    };

    const handlePointerMove = (event: React.PointerEvent) => {
      const gesture = gestureRef.current;
      if (!gesture) return;
      const current = pointerMinutes(event.clientY);

      if (gesture.kind === "create") {
        setDraft({
          start: Math.min(gesture.anchorMin, current),
          end: Math.max(gesture.anchorMin, current),
        });
        return;
      }

      if (gesture.kind === "move") {
        // Track the day column under the pointer so a horizontal drag can drop
        // the block onto another weekday. Do this before any vertical early
        // return so a purely sideways drag is still detected.
        const overDate = dateAtPoint(event.clientX, event.clientY);
        if (overDate) gesture.targetDate = overDate;
        onHoverDate?.(
          overDate && overDate !== date.toISOString() ? overDate : null
        );

        const duration = gesture.origEnd - gesture.origStart;
        const delta = current - gesture.anchorMin;
        const start = Math.min(
          Math.max(gesture.origStart + delta, 0),
          DAY_MINUTES - duration
        );
        if (start !== gesture.origStart) {
          snapshotOnce();
          gesture.interval.setIntervalStartTime(timeOfDayFromMinutes(start));
          gesture.interval.setIntervalEndTime(
            timeOfDayFromMinutes(start + duration)
          );
        }
        return;
      }

      if (gesture.kind === "resize-start") {
        snapshotOnce();
        const start = Math.min(current, gesture.origEnd - MIN_DURATION_MINUTES);
        gesture.interval.setIntervalStartTime(
          timeOfDayFromMinutes(Math.max(start, 0))
        );
        return;
      }

      // resize-end
      snapshotOnce();
      const end = Math.max(current, gesture.origStart + MIN_DURATION_MINUTES);
      gesture.interval.setIntervalEndTime(
        timeOfDayFromMinutes(Math.min(end, DAY_MINUTES))
      );
    };

    const endGesture = (event: React.PointerEvent) => {
      const gesture = gestureRef.current;
      gestureRef.current = null;
      onHoverDate?.(null);
      try {
        containerRef.current?.releasePointerCapture(event.pointerId);
      } catch {
        // Ignore if capture was never established.
      }

      if (gesture?.kind === "create" && draft) {
        let { start, end } = draft;
        if (end - start < MIN_DURATION_MINUTES) {
          // Treat a tap (no meaningful drag) as a default one-hour block.
          start = Math.min(start, DAY_MINUTES - 60);
          end = start + 60;
        }
        snapshotOnce();
        rule.addIntervalWithTimes(
          timeOfDayFromMinutes(start),
          timeOfDayFromMinutes(end)
        );
      } else if (gesture?.kind === "move") {
        const targetIso = gesture.targetDate;
        const movedToOtherDay = !!targetIso && targetIso !== date.toISOString();
        const current = intervalMinutes(gesture.interval);
        const timeUnchanged =
          current &&
          current.start === gesture.origStart &&
          current.end === gesture.origEnd;

        if (movedToOtherDay) {
          // Relocate the block onto the day it was released over, keeping its
          // (possibly newly dragged) times. Persisted intervals are marked for
          // destroy on the source rule and re-created on the target rule so the
          // move round-trips through nested attributes on save.
          snapshotOnce();
          const { rule: targetRule } = schedule.effectiveRuleForDate(
            new Date(targetIso)
          );
          const { startTime, endTime } = gesture.interval;
          rule.removeInterval(gesture.interval);
          if (startTime && endTime) {
            targetRule.addIntervalWithTimes(
              new Date(startTime),
              new Date(endTime)
            );
          }
        } else if (timeUnchanged) {
          // A press that didn't move at all is a click → open the popover to
          // fine-tune exact times / delete.
          onEditInterval({
            anchorEl: gesture.element,
            interval: gesture.interval,
            rule,
            date,
            isOverride,
          });
        }
      }
      setDraft(null);
    };

    const blocks = rule.presentIntervals
      .map((interval) => ({ interval, minutes: intervalMinutes(interval) }))
      .filter((entry) => entry.minutes !== null);

    return (
      <div
        ref={containerRef}
        data-date={date.toISOString()}
        className={cx(
          classes.column,
          isToday && classes.today,
          isDropTarget && classes.dropTarget
        )}
        style={{ height: COLUMN_HEIGHT }}
        onPointerDown={handleBackgroundPointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endGesture}
        onPointerCancel={endGesture}
      >
        {blocks.map(({ interval, minutes }) => (
          <CalendarEventBlock
            key={interval.id ?? interval.key}
            interval={interval}
            isOverride={isOverride}
            topPx={minutesToOffset(minutes!.start)}
            heightPx={minutesToOffset(minutes!.end - minutes!.start)}
            onGestureStart={(event, mode) =>
              handleGestureStart(event, mode, interval)
            }
            onDelete={() => {
              onBeforeMutate();
              rule.removeInterval(interval);
            }}
          />
        ))}

        {draft && draft.end > draft.start && (
          <div
            className={classes.draft}
            style={{
              top: minutesToOffset(draft.start),
              height: minutesToOffset(draft.end - draft.start),
            }}
          />
        )}
      </div>
    );
  }
);

const useStyles = makeStyles()(() => ({
  column: {
    position: "relative",
    flex: 1,
    minWidth: 0,
    borderRight: "1px solid #DEE6F0",
    backgroundImage: "linear-gradient(#EDF0F5 1px, transparent 1px)",
    backgroundSize: `100% ${HOUR_HEIGHT}px`,
    touchAction: "none",
  },
  today: {
    backgroundColor: "rgba(61,109,240,0.04)",
  },
  dropTarget: {
    backgroundColor: "rgba(46,158,91,0.12)",
    boxShadow: "inset 0 0 0 2px #2E9E5B",
  },
  draft: {
    position: "absolute",
    left: 2,
    right: 2,
    borderRadius: 6,
    backgroundColor: "rgba(61,109,240,0.25)",
    border: "1px dashed #3D6DF0",
    pointerEvents: "none",
  },
}));

export default CalendarDayColumn;
